.dialog {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 24px;
  border: 0;
  background: transparent;
  color: var(--text);
  overflow: hidden;
  overflow: clip;
  overscroll-behavior: contain;
  display: grid;
  place-items: center;
  font-family: var(--font-body);
  font-weight: 300;
  font-size: 15px;
  line-height: 1.55;
  --ease: cubic-bezier(0.2, 0.8, 0.3, 1);
  --ease-exit: cubic-bezier(0.4, 0, 0.9, 0.5);
  --from: translateY(12px) scale(0.96);
}

.dialog:not([open]) {
  display: none;
}

.dialog::backdrop {
  background: transparent;
}

.scrim {
  position: absolute;
  inset: 0;
  background: var(--scrim);
  opacity: 0;
  touch-action: none;
  -webkit-backdrop-filter: blur(6px) saturate(1.05);
  backdrop-filter: blur(6px) saturate(1.05);
  transition: opacity 220ms ease-in;
}

.dialog[data-state='open'] .scrim {
  opacity: 1;
  transition: opacity 260ms ease-out;
}

.panel {
  --w: 560px;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: min(var(--w), 100%);
  max-height: min(780px, calc(100dvh - 48px));
  background: var(--bg-raised);
  border: 1px solid var(--line);
  border-radius: 28px;
  box-shadow:
    0 30px 80px rgb(0 0 0 / 0.28),
    0 2px 10px rgb(0 0 0 / 0.08);
  outline: none;
  opacity: 0;
  transform: var(--from);
  transform-origin: 50% 50%;
  transition:
    opacity 160ms ease-in 40ms,
    transform 200ms var(--ease-exit);
}

.dialog[data-state='open'] .panel {
  opacity: 1;
  transform: none;
  transition:
    opacity 200ms ease-out,
    transform 420ms var(--ease);
}

.dialog[data-size='sm'] .panel {
  --w: 440px;
}

.dialog[data-size='lg'] .panel {
  --w: 720px;
}

.form {
  display: contents;
}

.head {
  flex: none;
  display: grid;
  gap: 6px;
  padding: 28px 72px 10px 28px;
  transition: box-shadow 0.2s;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 27px;
  line-height: 1.2;
  text-wrap: balance;
}

.description {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
}

.body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px 28px 8px;
  scrollbar-width: none;
}

.body::-webkit-scrollbar {
  display: none;
}

.foot {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
  padding: 18px 28px 26px;
  transition: box-shadow 0.2s;
}

.head + .foot {
  padding-top: 22px;
}

.panel[data-top='false'] .head {
  box-shadow: 0 1px 0 var(--line);
}

.panel[data-end='false'] .foot {
  box-shadow: 0 -1px 0 var(--line);
}

.panel .close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: transparent;
  background-image: none;
  box-shadow: none;
  color: var(--muted);
  cursor: pointer;
  opacity: 1;
  transition:
    background-color 0.2s,
    color 0.2s,
    transform 0.15s var(--ease),
    opacity 0.2s;
}

.panel .close svg {
  display: block;
  transition: transform 0.35s var(--ease);
}

.panel .close:hover:not(:disabled) {
  background: var(--pill-bg);
  color: var(--text);
  opacity: 1;
}

.panel .close:hover:not(:disabled) svg {
  transform: rotate(90deg);
}

.panel .close:active:not(:disabled) {
  transform: scale(0.9);
}

.panel .close:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.panel .close:disabled {
  opacity: 0.3;
  cursor: default;
}

.grab {
  display: none;
}

.dialog[data-mode='sheet'] {
  padding: 0;
  place-items: end stretch;
  --from: translateY(100%);
}

.dialog[data-mode='sheet'] .panel {
  width: 100%;
  max-height: calc(var(--vvh, 100dvh) - var(--inset-top, env(safe-area-inset-top, 0px)) - 40px);
  margin-bottom: var(--kb, 0px);
  border-radius: 28px 28px 0 0;
  border-bottom: 0;
  opacity: 1;
  padding-bottom: var(--inset-bottom, env(safe-area-inset-bottom, 0px));
  transition: transform 260ms cubic-bezier(0.4, 0, 1, 1);
}

.dialog[data-mode='sheet'][data-state='open'] .panel {
  transition: transform 460ms var(--ease);
}

.dialog[data-mode='sheet'] .grab {
  display: block;
  flex: none;
  width: 40px;
  height: 5px;
  margin: 9px auto 0;
  border-radius: 3px;
  background: var(--muted-dim);
  opacity: 0.45;
  touch-action: none;
  cursor: grab;
}

.dialog[data-mode='sheet'] .head {
  padding: 12px 64px 6px 20px;
  touch-action: none;
  cursor: grab;
}

.dialog[data-mode='sheet'] .title {
  font-size: 23px;
}

.dialog[data-mode='sheet'] .body {
  padding: 8px 20px;
}

.dialog[data-mode='sheet'] .foot {
  padding: 14px 20px 18px;
  flex-wrap: nowrap;
}

.dialog[data-mode='sheet'] .foot > * {
  flex: 1 1 0;
  min-width: 0;
}

.dialog[data-mode='sheet'] .panel .close {
  top: 14px;
  right: 12px;
}

@media (prefers-reduced-motion: reduce) {
  .dialog {
    --from: none;
  }

  .panel,
  .dialog[data-mode='sheet'] .panel {
    opacity: 0;
    transform: none;
    transition: opacity 140ms ease;
  }

  .dialog[data-state='open'] .panel {
    opacity: 1;
  }
}
